/**
 * A nightstand the stager brings, where the scan has none beside the bed.
 *
 * Stagers treat nightstands as part of the staged bedroom itself — the bed,
 * a nightstand each side, a dresser (Apartment Therapy, West RE) — and the
 * lamp and the low vignette need something to stand on. So a bed with no
 * nightstand on a side that wants one gets the corpus-median piece, standing
 * where the scanned ones stand: 2 in off the bed's side, its back to the head
 * wall. It is a staged prop like any other: Remove accessories takes
 * it away again.
 *
 * Only where it plainly fits: the whole footprint on the floor, clear of the
 * room's own furniture and of every door's swing. Otherwise the side stays
 * bare, which is what it was.
 */

const { footprintFor } = require('../../../geometry/objectFootprint');
const {
  doPolygonsOverlap,
} = require('../../../bathroom-analyzer/pipeline/geometry');
const {
  isPointInsideOrOnPolygon,
} = require('../../../geometry/pointInPolygon');
const { objectsYOverlap } = require('../../../geometry/verticalOverlap');
const { objectWorldFromLocal } = require('../../../dorm-styling/plan/shared');
const { getFloorPolygon } = require('../../../dorm-styling/dormDecorGeometry');
const { propBox } = require('../../bed/dressBed');
const { doorSwingZones, footprintClearsDoorSwings } = require('../doorSwing');
const { bedBaseY } = require('./bedHost');
const { RUG_FLOOR_SLACK_M } = require('./constants');

const IN = 0.0254;
/** Off the bed's side, as the scanned ones stand. */
const SIDE_GAP_M = 2 * IN;
/** Off the head wall's face, when the bed has one. */
const WALL_GAP_M = 0.02;

/**
 * @returns {{ fields: object, box: object } | null}  the record's fields, and
 *   its top in the bed frame (the box `dressNightstand` takes)
 */
/**
 * @param {number|null} headGap  the bed head's gap to its wall's face (from
 *   `findHeadWall`), or null for a bed standing in the room — the
 *   nightstand's back then lines up with the head end
 */
function placeStagedNightstand(room, bed, side, headGap, planned = []) {
  const dims = propBox('staging-nightstand');
  const floor = getFloorPolygon(room);
  if (!floor?.length) return null;
  const halfW = bed.dimensions.x / 2;
  const x = side * (halfW + SIDE_GAP_M + dims.x / 2);
  // Back to the head wall where there is one — a scanned bed's head often
  // touches its wall's face or sits a scan's slack into it, and a box set
  // behind the head end would stand in the wall.
  const back =
    -bed.dimensions.z / 2 -
    (headGap == null ? 0 : Math.max(0, headGap - WALL_GAP_M));
  const z = back + dims.z / 2;
  const ry = bed.rotation?.y || 0;
  const at = objectWorldFromLocal(bed, x, z);
  const base = bedBaseY(bed);
  const fields = {
    center: { x: at.x, y: base + dims.y / 2, z: at.z },
    dimensions: dims,
    rotation: { x: 0, y: ry, z: 0 },
  };
  const f = footprintFor(at, dims.x, dims.z, ry);
  if (!f) return null;
  if (!f.every((p) => isPointInsideOrOnPolygon(p, floor, RUG_FLOOR_SLACK_M)))
    return null;
  const blocked = [...(room.objects || []), ...planned].some((o) => {
    if (o === bed || !o?.center || !o?.dimensions) return false;
    if (!objectsYOverlap(fields, o, { missingHeight: 'full' })) return false;
    const g = footprintFor(
      o.center,
      o.dimensions.x,
      o.dimensions.z,
      o.rotation?.y || 0
    );
    return g && doPolygonsOverlap(f, g);
  });
  if (blocked) return null;
  if (!footprintClearsDoorSwings(f, doorSwingZones(room))) return null;
  const outer = side * (halfW + SIDE_GAP_M + dims.x);
  const inner = side * (halfW + SIDE_GAP_M);
  return {
    fields,
    box: { outer, inner, back, front: back + dims.z, top: dims.y },
  };
}

module.exports = { placeStagedNightstand };
